import { useGetPatientLookupQuery } from '@app/main/api';
import { formatters, notify } from '@app/ui';
import { useUserContextSelector } from '@app/main/utils';
import { chatifyApi } from '@webinex/chatify';
import { isEmpty, uniq } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CancelAppointmentChatForm, CancelAppointmentChatFormValue } from './CancelAppointmentChatForm';
import { useActiveCoordinatorIds } from './useActiveCoordinatorIds';

export interface CancelAppointmentChatWidgetProps {
  visitorId: string;
  appointmentStart: string;
  onClose: () => void;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

function useChatMembers() {
  const currentUserId = useUserContextSelector((x) => x.id);
  const { ids: coordinatorIds, isLoading } = useActiveCoordinatorIds();

  const memberIds = useMemo(() => {
    if (!currentUserId || !coordinatorIds) return undefined;

    return uniq([currentUserId, ...coordinatorIds]);
  }, [currentUserId, coordinatorIds]);

  return { memberIds, isLoading };
}

function useChatName(visitorId: string, appointmentStart: string) {
  const { t } = useTranslation(undefined, { keyPrefix: 'cancelAppointmentChat' });
  const { data: patient, isLoading } = useGetPatientLookupQuery({ id: visitorId });

  const chatName = useMemo(() => {
    if (!patient) return undefined;

    return t('chatNameFormat', {
      patient: formatters.name(patient),
      date: formatters.date(appointmentStart, 'date-time'),
    });
  }, [patient, appointmentStart, t]);

  return { chatName, isLoading };
}

function useSubmit(
  props: CancelAppointmentChatWidgetProps,
  memberIds: string[] | undefined,
  chatName: string | undefined,
) {
  const { visitorId, appointmentStart, onClose } = props;
  const [addChat] = chatifyApi.useAddChatMutation();
  const [sendChatMessage] = chatifyApi.useSendChatMessageMutation();

  return useCallback(
    async (value: CancelAppointmentChatFormValue) => {
      if (!visitorId || !appointmentStart) return;
      if (!memberIds || isEmpty(memberIds) || !chatName) {
        notify.error.t('cancelAppointmentChat.noCoordinators');
        return;
      }

      const chatId = await addChat({ name: chatName, members: memberIds }).unwrap();
      await sendChatMessage({ chatId, text: value.reason, files: [] }).unwrap();

      onClose();
      notify.success.t('cancelAppointmentChat.created');
    },
    [visitorId, appointmentStart, memberIds, chatName, addChat, sendChatMessage, onClose],
  );
}

export function CancelAppointmentChatWidget(props: CancelAppointmentChatWidgetProps) {
  const { visitorId, appointmentStart, onCanSubmitChange } = props;
  const { memberIds, isLoading: isChatMembersLoading } = useChatMembers();
  const { chatName, isLoading: isChatNameLoading } = useChatName(visitorId, appointmentStart);
  const onSubmit = useSubmit(props, memberIds, chatName);

  const canSubmit = !isChatMembersLoading && !isChatNameLoading && !isEmpty(memberIds) && !!chatName;

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  return <CancelAppointmentChatForm value={CancelAppointmentChatForm.initialValues} onSubmit={onSubmit} />;
}
